import * as React from 'react';
import { Alert, Button, Form, FormGroup, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { HttpRequest } from '../../util/http';
import { userData } from '../../util/userData';
import { ProjectSelect } from './lib/ProjectSelect';
import { QuestionSelect } from './lib/QuestionSelect';
import { SurveySelect } from './lib/SurveySelect';
import { GetAllQuestions, IQuestion } from './lib/utils';

interface IImportQuestionState {
  isOpen: boolean
  newQuestionId: string
  submitting: boolean
  success: boolean
  submitError: string | undefined
  selectedSurveyAbbr: string | undefined
  questionId: string | undefined
  selectedQuestion: IQuestion | undefined
  projectId: string | undefined
  clientName: string
}

export class ImportQuestion extends React.Component<{}, IImportQuestionState> {
  initState: IImportQuestionState = {
    isOpen: false,
    submitting: false,
    success: false,
    newQuestionId: '',
    submitError: undefined,
    selectedSurveyAbbr: undefined,
    questionId: undefined,
    selectedQuestion: undefined,
    projectId: undefined,
    clientName: ''
  }

  state: IImportQuestionState = { ...this.initState }

  questions: IQuestion[] = []

  currentSurveyAbbr: string = ''

  componentWillMount() {
    this.getAllQuestionsForCurrentClient()
  }

  getAllQuestionsForCurrentClient(force = false) {
    const currentSurveyAbbr = userData.getCurrentSurveyAbbr()

    if (!force && this.currentSurveyAbbr === currentSurveyAbbr) { return }

    this.currentSurveyAbbr = currentSurveyAbbr
    this.questions = []

    GetAllQuestions(currentSurveyAbbr).then(({ values: questions, clientName }) => {
      this.questions = questions
      this.setState({ clientName })
    })
  }

  /**
   * Open/Close Modal
   * Note: upon close the state will be reset
   */
  toggleModal(isOpen = !this.state.isOpen) {
    const shouldReturnToInitState = !isOpen

    if (shouldReturnToInitState) {
      const { clientName } = this.state //keep client name
      //this.questions = []
      this.state = { ...this.initState, clientName }
      this.setState(this.state)
    } else {
      this.getAllQuestionsForCurrentClient()
      this.setState({ isOpen })
    }
  }

  /**
   * Is a new Qid unique to the currently selected survey
   */
  isUniqueQid(newQid: string) {
    return this.questions.findIndex((q) => q.qid === newQid) < 0
  }

  /**
   * Sets/removes error message from NewQid input element
   * If qid is not unique an error message will be added
   */
  setCustomValidityOnNewQid(e: React.KeyboardEvent<HTMLInputElement>) {
    const newQidElem = e.currentTarget
    const isUniqueQid = this.isUniqueQid(newQidElem.value)
    const emptyMessage = ''
    const nonUniqueMessage = 'Qid is not unique. Update and press Import again.'
    const customMessage = isUniqueQid ? emptyMessage : nonUniqueMessage

    newQidElem.setCustomValidity(customMessage)
  }

  /**
   * Initiate import
   * @param e
   */
  submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    this.setState({ submitting: true, submitError: undefined })

    const { newQuestionId, selectedQuestion, projectId, selectedSurveyAbbr } = this.state

    if (!selectedQuestion) { return }

    // tslint:disable:variable-name
    const copy_client = projectId;
    const copy_survey = selectedSurveyAbbr;
    const questionToCopy = selectedQuestion;
    const currentSurveyAbbr = userData.getCurrentSurveyAbbr();
    const data = { newQuestionId, questionToCopy, copy_client, copy_survey, currentSurveyAbbr }
    const method = 'POST'

    HttpRequest.fetch('/scripts/questions/copy', { data, method })
      .then((res) => {
        if (typeof window.loadQuestions === 'function') {
          window.loadQuestions()
        }

        this.getAllQuestionsForCurrentClient(true);
        this.setState({ success: true, submitting: false })
      })
      .catch((err) => {
        this.setState({ submitError: 'Unable to preform import.', submitting: false })
      })
  }

  updateQuestionId(questionId: string, selectedQuestion: IQuestion) {
    this.setState({ questionId, selectedQuestion })

    const questionIdNumberMatch = questionId.match(/\D+(\d+)$/)
    let newQuestionId = ''
    let nextQuestionNumber = 1;
    let questionPart = questionId;

    if (questionIdNumberMatch) {
      // if a terminating number is found, increment
      const questionNumberStr = questionIdNumberMatch[1] //match the first capture group
      const questionNumberPosition = questionId.lastIndexOf(questionNumberStr)
      const questionNumber = Number(questionNumberStr)

      questionPart = questionId.substring(0, questionNumberPosition)
      nextQuestionNumber = questionNumber + 1;
    } else {
      questionPart += '_';
    }

    // loop until unique increment exists
    do {
      newQuestionId = questionPart + nextQuestionNumber

      nextQuestionNumber++;
    } while (!this.isUniqueQid(newQuestionId))

    this.setState({ newQuestionId })
  }

  render() {
    const { submitting, success } = this.state
    const disableActions = submitting || success

    return (
      <>
        <Button className="waves-effect waves-light" type="button"
          onClick={() => this.toggleModal()}>
          <i className="fas fa-download fa-fw" />
          <span>&nbsp;Import</span>
        </Button>

        <Modal wrapClassName="blacklight" modalClassName="blacklight-modal" isOpen={this.state.isOpen} toggle={() => this.toggleModal()}>
          <Form onSubmit={(e) => this.submit(e)}>
            <header className="bg-primary text-white p-4 rounded-top">
              <h2 className="text-center mb-4">
                <i className="fas fa-fw fa-download" />
                <span>&nbsp;Import a Question</span>
              </h2>
              <article>Fill out the form below to import a question into {this.state.clientName}.</article>
            </header>
            <ModalBody>
              <ProjectSelect required
                onChange={(projectId) => this.setState({ projectId })}
                value={this.state.projectId} />

              <SurveySelect required
                projectId={this.state.projectId}
                onChange={(selectedSurveyAbbr) => this.setState({ selectedSurveyAbbr })}
                value={this.state.selectedSurveyAbbr} />

              <QuestionSelect required
                projectId={this.state.projectId}
                surveyAbbr={this.state.selectedSurveyAbbr}
                onChange={(questionId, selectedQuestion) => this.updateQuestionId(questionId, selectedQuestion)}
                value={this.state.questionId} />

              <FormGroup>
                <label>New Question Id (Qid)</label>
                <Input type="text" required
                  disabled={!this.state.questionId}
                  onKeyUp={(e) => this.setCustomValidityOnNewQid(e)}
                  maxLength={10}
                  onChange={({ currentTarget }) => this.setState({ newQuestionId: currentTarget.value })}
                  value={this.state.newQuestionId} />
              </FormGroup>
            </ModalBody>
            {this.state.submitError &&
              <Alert color="danger" className="mb-0">
                <i className="fas fa-fw fa-exclamation-triangle" />
                <span>&nbsp;{this.state.submitError}</span>
              </Alert>
            }
            {!this.state.success &&
              <ModalFooter>
                <Button type="button" color="white" onClick={() => this.toggleModal()}
                  disabled={disableActions}>
                  <span>Cancel</span>
                </Button>
                <Button type="submit" color="action"
                  disabled={disableActions}>
                  {this.state.submitting ?
                    <i key="Submitting" className="fas fa-cog fa-spin fa-fw" />
                    :
                    <i key="Import_CTA" className="fas fa-download fa-fw" />
                  }
                  <span>&nbsp;Import</span>
                </Button>
              </ModalFooter>
            }
            {this.state.success &&
              <Alert color="success" className="mb-0">
                <i className="fas fa-fw fa-check" />
                <span>&nbsp;Import was successful!&nbsp;</span>
                <a href="javascript:void(0)" onClick={() => this.toggleModal()}>Click here to close.</a>
              </Alert>
            }
          </Form>
        </Modal>
      </>
    )
  }
}
